/**
 * Favorites Page - Display user's favorite products
 * Requires user authentication to access
 */

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Chip,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ShoppingCart as ShoppingCartIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import ProductCard from '../components/Products/ProductCard';
import {
  loadUserFavorites,
  toggleFavorite,
  selectFavorites,
  selectFavoritesCount,
  selectFavoritesLoading,
  selectFavoritesError
} from '../store/slices/favoritesSlice';
import { addToCart } from '../store/slices/cartSlice';

const FavoritesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Redux state
  const favorites = useSelector(selectFavorites);
  const favoritesCount = useSelector(selectFavoritesCount);
  const isLoading = useSelector(selectFavoritesLoading);
  const error = useSelector(selectFavoritesError);
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const user = useSelector(state => state.auth.user);
  
  // Local state
  const [removingItemId, setRemovingItemId] = useState(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { 
        state: { 
          from: '/favorites',
          message: 'Please login to view your favorite products'
        }
      });
      return;
    }

    // Load favorites when component mounts
    dispatch(loadUserFavorites());
  }, [isAuthenticated, dispatch, navigate]);

  const handleRemoveFromFavorites = async (product) => {
    try {
      setRemovingItemId(product.id);
      await dispatch(toggleFavorite(product.id)).unwrap();
    } catch (error) {
      console.error('Error removing from favorites:', error);
    } finally {
      setRemovingItemId(null);
    }
  };

  const handleAddToCart = (product) => {
    dispatch(addToCart({ product, quantity: 1 }));
  };

  const handleGoShopping = () => {
    navigate('/products');
  };

  const handleProductClick = (product) => {
    navigate(`/products/${product.id}`);
  };

  // Loading state
  if (isLoading && favorites.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => dispatch(loadUserFavorites())}>
          Try Again
        </Button>
      </Container>
    );
  }

  // Empty state
  if (!isLoading && favorites.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="text.primary">Favorites</Typography>
        </Breadcrumbs>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 6, 
            textAlign: 'center',
            backgroundColor: 'grey.50',
            border: '2px dashed',
            borderColor: 'grey.300'
          }}
        >
          <FavoriteBorderIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
          <Typography variant="h4" gutterBottom color="text.secondary">
            No favorites yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
            Start exploring our fresh farm products and add them to your favorites 
            by clicking the heart icon on any product card.
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            startIcon={<ShoppingCartIcon />}
            onClick={handleGoShopping}
            sx={{ mt: 2 }}
          >
            Start Shopping
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="text.primary">Favorites</Typography>
        </Breadcrumbs>

        {/* Page Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FavoriteIcon color="error" sx={{ mr: 2, fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              My Favorites
            </Typography>
          </Box>
          <Chip 
            label={`${favoritesCount} item${favoritesCount !== 1 ? 's' : ''}`}
            color="primary"
            variant="outlined"
          />
        </Box>

        {user && (
          <Typography variant="body1" color="text.secondary">
            Welcome back, {user.name || user.email}! Here are your favorite products.
          </Typography>
        )}
      </Box>

      {/* Action Bar */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          variant="outlined"
        >
          Back
        </Button>
        
        <Button
          variant="contained"
          startIcon={<ShoppingCartIcon />}
          onClick={handleGoShopping}
        >
          Continue Shopping
        </Button>
      </Box>

      {/* Products Grid */}
      <Grid container spacing={3}>
        {favorites.map((favorite) => {
          const product = favorite.product;
          if (!product) return null;

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={favorite.id}>
              <Box 
                sx={{ 
                  position: 'relative',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    transition: 'transform 0.2s ease-in-out'
                  }
                }}
                onClick={() => handleProductClick(product)}
              >
                <ProductCard
                  product={product}
                  onAddToCart={handleAddToCart}
                  onToggleFavorite={() => handleRemoveFromFavorites(product)}
                  variant="default"
                  showActions={true}
                />
                
                {/* Remove from favorites overlay */}
                {removingItemId === product.id && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1,
                      zIndex: 10
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* Loading more indicator */}
      {isLoading && favorites.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Call to action */}
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Looking for more products?
        </Typography>
        <Button 
          variant="outlined" 
          size="large"
          onClick={handleGoShopping}
          sx={{ mt: 1 }}
        >
          Explore All Products
        </Button>
      </Box>
    </Container>
  );
};

export default FavoritesPage;